import { cn } from "@/lib/utils";

export function ChoiceCard({
  selected,
  title,
  description,
  onClick,
}: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <label
      className={cn(
        "relative flex h-[58px] cursor-pointer gap-2 rounded-[9px] border px-2.5 py-2 text-left outline-none transition-[background-color,border-color,box-shadow] has-focus-visible:ring-2 has-focus-visible:ring-ring",
        selected
          ? "border-ink bg-surface shadow-[inset_0_0_0_0.5px_var(--color-ink)]"
          : "border-line bg-surface hover:bg-subtle/60",
      )}
    >
      <input type="radio" checked={selected} onChange={onClick} className="sr-only" />
      <span
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-ink" : "border-line",
        )}
      >
        {selected ? <span className="size-2 rounded-full bg-ink" /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold leading-4 text-ink">{title}</span>
        <span className="block text-[9px] leading-4 text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

export function ModeCard({
  selected,
  title,
  description,
  badge,
  onClick,
}: {
  selected: boolean;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <label
      className={cn(
        "relative flex h-14 cursor-pointer flex-col justify-center rounded-[9px] border px-2.5 outline-none transition-[background-color,border-color,box-shadow] has-focus-visible:ring-2 has-focus-visible:ring-ring",
        selected
          ? "border-ink shadow-[inset_0_0_0_0.5px_var(--color-ink)]"
          : "border-line hover:bg-subtle/60",
      )}
    >
      <input type="radio" checked={selected} onChange={onClick} className="sr-only" />
      <span className="flex items-center justify-between text-[10px] font-semibold text-ink">
        <span>{title}</span>
        {badge ? (
          <span className="text-[8px] font-normal text-muted-foreground">{badge}</span>
        ) : null}
      </span>
      <span className="mt-0.5 text-[9px] text-muted-foreground">{description}</span>
    </label>
  );
}

export function ReviewMetric({
  label,
  value,
  large = false,
  dot = false,
}: {
  label: string;
  value: string;
  large?: boolean;
  dot?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col justify-center px-4">
      <p className="text-[9px] text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 truncate text-[11px] font-semibold text-ink",
          large && "text-[18px] leading-5",
        )}
      >
        {dot ? <span className="mr-1.5 inline-block size-1.5 rounded-full bg-amber" /> : null}
        {value}
      </p>
    </div>
  );
}

export function CoverageMetric({
  value,
  label,
  muted = false,
}: {
  value: number;
  label: string;
  muted?: boolean;
}) {
  return (
    <div className="w-12 text-center">
      <p
        className={cn(
          "text-[16px] font-semibold leading-5 tabular-nums text-ink",
          muted && "text-muted-foreground",
        )}
      >
        {value}
      </p>
      <p className="text-[9px] text-muted-foreground">{label}</p>
    </div>
  );
}
